import {lazy, ComponentType} from 'react';
import Authorization from './lib/Authorization';
import RoleNames from './lib/RoleNames';
import {NavigationRoutes} from './NavigationRoutes';

export interface Page extends Authorization {
    path: string;
    /**
     * Every page is rendered by the router with no props, so the component only has to be
     * one that accepts none. Deliberately not ComponentType<any>, which would also accept a
     * component requiring props the router never supplies.
     */
    component: React.LazyExoticComponent<ComponentType<Record<string, never>>>;
    label?: string; // Display label for menus
    children?: Page[];
    index?: boolean;
    hideFromMenu?: boolean; // Don't show in navigation menus
    section?: string; // For grouping (e.g., 'monitoring', 'config')
    featureFlag?: string; // Feature flag requirement
    requiresMultiProvider?: boolean; // Only available when multi-provider support is enabled (client config)
    isThetaAdminOnly?: boolean; // Special case for Theta-specific features
    noLayout?: boolean; // Don't wrap in Layout component (for pages with their own layout)
}

// Admin index redirect component
const AdminIndexRedirect = lazy(
    () => import('./components/admin/AdminIndexRedirect')
);

// Lazy load components
const HomePage = lazy(() => import('./components/HomePage'));
const ErrorBoundaryTestPage = lazy(
    () => import('./components/ErrorBoundaryTestPage')
);
const LibraryPage = lazy(() => import('./components/library/LibraryPage'));
const ProfilePage = lazy(() => import('./components/profile/ProfilePage'));
const PersonalAccessTokensPage = lazy(
    () => import('./components/profile/PersonalAccessTokensPage')
);
const ThreadPage = lazy(() => import('./components/threads/ThreadPage'));
const JobsPage = lazy(() => import('./components/jobs/JobsPage'));
const JobDetailPage = lazy(() => import('./components/jobs/JobDetailPage'));
const AssistantPage = lazy(() => import('./components/AssistantPage'));
const RedeemShareLinkPage = lazy(
    () => import('./components/RedeemShareLinkPage')
);
const AdminSettingsPage = lazy(
    () => import('./components/admin/AdminSettingsPage')
);
const AdminBrandingPage = lazy(
    () => import('./components/admin/AdminBrandingPage')
);
const AdminUsersPage = lazy(() => import('./components/admin/AdminUsersPage'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AssistantLayout = lazy(
    () => import('./components/assistant/AssistantLayout')
);
const ApplicationSecrets = lazy(
    () => import('./components/admin/ApplicationSecrets')
);
const AdminFeatureFlagsPage = lazy(
    () => import('./components/admin/AdminFeatureFlagsPage')
);
const AdminToolSettingsPage = lazy(
    () => import('./components/admin/AdminToolSettingsPage')
);
const AdminTeamsPage = lazy(() => import('./components/admin/AdminTeamsPage'));
const AdminAssistantDefaultsPage = lazy(
    () => import('./components/admin/AdminAssistantDefaultsPage')
);
const AdminWebSearchToolSettingsPage = lazy(
    () => import('./components/admin/AdminWebSearchToolSettingsPage')
);
const AdminMicrosoftGraphApiToolSettingsPage = lazy(
    () => import('./components/admin/AdminMicrosoftGraphApiToolSettingsPage')
);
const AdminM365ToolSettingsPage = lazy(
    () => import('./components/admin/AdminM365ToolSettingsPage')
);
const AdminPlannerToolSettingsPage = lazy(
    () => import('./components/admin/AdminPlannerToolSettingsPage')
);
const AdminDriveSyncSettingsPage = lazy(
    () => import('./components/admin/AdminDriveSyncSettingsPage')
);
const AdminHelpfruitToolSettingsPage = lazy(
    () => import('./components/admin/AdminHelpfruitToolSettingsPage')
);
const AdminBuiltInToolsSettingsPage = lazy(
    () => import('./components/admin/AdminBuiltInToolsSettingsPage')
);
const AdminConnectorSettingsPage = lazy(
    () => import('./components/admin/AdminConnectorSettingsPage')
);
const AdminAuthConfigsPage = lazy(
    () => import('./components/admin/AdminAuthConfigsPage')
);
const AdminMcpPage = lazy(() => import('./components/admin/AdminMcpPage'));
const AdminSkillsPage = lazy(
    () => import('./components/admin/AdminSkillsPage')
);
const AdminMcpActivityLogsPage = lazy(
    () => import('./components/admin/AdminMcpActivityLogsPage')
);
const AdminPublisherSettingsPage = lazy(
    () => import('./components/admin/AdminPublisherSettingsPage')
);
const AdminFileSyncOperationsPage = lazy(
    () => import('./components/admin/AdminFileSyncOperationsPage')
);
const AdminDriveSyncsPage = lazy(
    () => import('./components/admin/AdminDriveSyncsPage')
);
const AdminBackgroundOperationsPage = lazy(
    () => import('./components/admin/AdminBackgroundOperationsPage')
);
const AdminJobDetailPage = lazy(
    () => import('./components/admin/AdminJobDetailPage')
);
const AdminBillingPage = lazy(
    () => import('./components/admin/AdminBillingPage')
);
const AdminUserDetailPage = lazy(
    () => import('./components/admin/AdminUserDetailPage')
);
const AdminProfileChangeLogPage = lazy(
    () => import('./components/admin/AdminProfileChangeLogPage')
);
const AnalyticsLayout = lazy(
    () => import('./components/analytics/AnalyticsLayout')
);
const AnalyticsIndexRedirect = lazy(
    () => import('./components/analytics/AnalyticsIndexRedirect')
);
const ActivityPage = lazy(() => import('./components/analytics/ActivityPage'));
const TokenUsagePage = lazy(
    () => import('./components/analytics/TokenUsagePage')
);
const RoiPage = lazy(() => import('./components/analytics/RoiPage'));
const LegacyAnalyticsRedirect = lazy(
    () => import('./components/admin/LegacyAnalyticsRedirect')
);
const AdminApprovalsPage = lazy(
    () => import('./components/admin/AdminApprovalsPage')
);
const SharingApprovalRedirect = lazy(
    () => import('./components/admin/SharingApprovalRedirect')
);
const AdminAssistantsPage = lazy(
    () => import('./components/admin/AdminAssistantsPage')
);
const ThreadRunErrorsPage = lazy(
    () => import('./components/admin/ThreadRunErrorsPage')
);
const ThreadRunDebugPage = lazy(
    () => import('./components/admin/ThreadRunDebugPage')
);
const AdminVectorStoresPage = lazy(
    () => import('./components/admin/AdminVectorStoresPage')
);
const AdminModelsPage = lazy(
    () => import('./components/admin/AdminModelsPage')
);
const AdminAiProvidersPage = lazy(
    () => import('./components/admin/AdminAiProvidersPage')
);
const AdminAiProviderDetailPage = lazy(
    () => import('./components/admin/AdminAiProviderDetailPage')
);
const AdminDeploymentTasksPage = lazy(
    () => import('./components/admin/AdminDeploymentTasksPage')
);
const AdminMigrateModelsPage = lazy(
    () => import('./components/admin/AdminMigrateModelsPage')
);
const AdminModelMigrationProgressPage = lazy(
    () => import('./components/admin/AdminModelMigrationProgressPage')
);
const AdminDiagnosticsPage = lazy(
    () => import('./components/admin/AdminDiagnosticsPage')
);
const AdminGroupsPage = lazy(
    () => import('./components/admin/AdminGroupsPage')
);
const AdminGroupDetailPage = lazy(
    () => import('./components/admin/AdminGroupDetailPage')
);
const AdminAccessProfilesPage = lazy(
    () => import('./components/admin/AdminAccessProfilesPage')
);
const AdminAccessProfileDetailPage = lazy(
    () => import('./components/admin/AdminAccessProfileDetailPage')
);
const AdminEntraIdSyncConfigPage = lazy(
    () => import('./components/admin/AdminEntraIdSyncConfigPage')
);
const AdminEntraIdSyncHistoryPage = lazy(
    () => import('./components/admin/AdminEntraIdSyncHistoryPage')
);
const AdminEntraIdSyncGroupFilterPage = lazy(
    () => import('./components/admin/AdminEntraIdSyncGroupFilterPage')
);

const assistantToolEditorRoles = [
    RoleNames.AssistantToolEditors,
    RoleNames.AssistantMCPEditors,
    RoleNames.SysAdmin
];

// Sub-paths under /assistants/:id/details. Route-level role gates mirror the
// nav gates in assistantEditSections.tsx for the items that have them.
const assistantEditDetailPaths: (Pick<Page, 'roles' | 'featureFlag'> & {
    path: string;
})[] = [
    {path: ''},
    {path: '/user-guide'},
    {path: '/share'},
    {path: '/files'},
    {path: '/certification'},
    {path: '/template'},
    {path: '/publishing'},
    {path: '/integration'},
    {path: '/mcp', roles: assistantToolEditorRoles},
    {path: '/auth', roles: assistantToolEditorRoles},
    {path: '/skills', roles: assistantToolEditorRoles, featureFlag: 'skills'},
    {path: '/tools'},
    {path: '/forms'},
    {path: '/package'},
    // Only reached with id '_': importing creates an assistant rather than editing one.
    {path: '/import'},
    {path: '/add-to-home'},
    {path: '/schedules'},
    {path: '/schedules/:scheduleId'},
    {path: '/drive-sync'},
    {path: '/drive-sync/:configId'},
    {path: '/batch-presets'},
    {path: '/batch-presets/:presetId'},
    {path: '/background-runs'},
    {path: '/webcrawl'},
    {path: '/webcrawl/history'},
    {path: '/webcrawl/history/:runId'},
    {path: '/sync-log'}
];

const assistantEditRoutes: Page[] = assistantEditDetailPaths.map(
    ({path, ...gates}) => ({
        path: `/assistants/:id/details${path}`,
        component: AssistantLayout,
        authorize: true,
        noLayout: true,
        ...gates
    })
);

const pages: Page[] = [
    {
        path: NavigationRoutes.home(),
        component: HomePage,
        label: 'Home',
        authorize: true
    },
    {
        path: NavigationRoutes.library(),
        component: LibraryPage,
        label: 'Library',
        authorize: true
    },
    {
        path: NavigationRoutes.profile(),
        component: ProfilePage,
        label: 'Profile',
        authorize: true,
        hideFromMenu: true
    },
    {
        path: NavigationRoutes.personalAccessTokens(),
        component: PersonalAccessTokensPage,
        label: 'Personal access tokens',
        authorize: true,
        hideFromMenu: true
    },
    {
        path: NavigationRoutes.threads(),
        component: ThreadPage,
        label: 'Threads',
        authorize: true
    },
    {
        path: NavigationRoutes.jobs.root(),
        component: JobsPage,
        label: 'Jobs',
        authorize: true
    },
    {
        path: NavigationRoutes.jobs.detail(':id'),
        component: JobDetailPage,
        label: 'Job Details',
        hideFromMenu: true,
        authorize: true
    },
    // Insights (the analytics dashboards): open to every signed-in user. Which rows they see
    // is decided by the scope switch in AnalyticsLayout and enforced server-side, not by a
    // role gate here.
    {
        path: NavigationRoutes.analytics.root(),
        component: AnalyticsLayout,
        label: 'Insights',
        authorize: true,
        children: [
            {
                path: '',
                component: AnalyticsIndexRedirect,
                authorize: true,
                index: true,
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.analytics.activity(),
                component: ActivityPage,
                label: 'Activity',
                authorize: true
            },
            {
                path: NavigationRoutes.analytics.tokens(),
                component: TokenUsagePage,
                label: 'Token Usage',
                authorize: true
            },
            {
                path: NavigationRoutes.analytics.roi(),
                component: RoiPage,
                label: 'ROI',
                authorize: true
            }
        ]
    },
    // Diagnostic route — deliberately crashes during render to exercise the recovery
    // screen via the data router's catch-all errorElement (RouteErrorBoundary).
    {
        path: '/error-test',
        component: ErrorBoundaryTestPage,
        label: 'Error Boundary Test',
        hideFromMenu: true,
        authorize: true
    },
    {
        path: '/admin',
        component: AdminLayout,
        label: 'Admin',
        authorize: true,
        children: [
            // Index route redirects to sharing approval
            {
                path: '',
                component: AdminIndexRedirect,
                authorize: true,
                roles: [RoleNames.SysAdmin, RoleNames.Analytics],
                index: true,
                hideFromMenu: true
            },
            // Configuration routes
            {
                path: NavigationRoutes.admin.settings(),
                component: AdminSettingsPage,
                label: 'Settings',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.branding(),
                component: AdminBrandingPage,
                label: 'Branding',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.assistantDefaults(),
                component: AdminAssistantDefaultsPage,
                label: 'Defaults',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.models(),
                component: AdminModelsPage,
                label: 'Models',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.aiProviders(),
                component: AdminAiProvidersPage,
                label: 'AI Providers',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                requiresMultiProvider: true
            },
            {
                // Not gated on multi-provider: the Models page lists the built-in catalog
                // too, and this route only decides which row opens its editor.
                path: NavigationRoutes.admin.modelDetail(':modelId'),
                component: AdminModelsPage,
                label: 'Model Details',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.admin.aiProviderDetail(':providerId'),
                component: AdminAiProviderDetailPage,
                label: 'AI Provider Details',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true,
                requiresMultiProvider: true
            },
            {
                path: NavigationRoutes.admin.migrateModels(),
                component: AdminMigrateModelsPage,
                label: 'Migrate Models',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                isThetaAdminOnly: true
            },
            {
                path: NavigationRoutes.admin.modelMigrationProgress(),
                component: AdminModelMigrationProgressPage,
                label: 'Model Migration Progress',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                isThetaAdminOnly: true,
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.admin.teams(),
                component: AdminTeamsPage,
                label: 'Microsoft Teams App',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.applicationSecrets(),
                component: ApplicationSecrets,
                label: 'Application Secrets',
                section: 'config',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                featureFlag: 'applicationsecrets'
            },
            // Approvals - Standalone
            {
                path: NavigationRoutes.admin.approvals(),
                component: AdminApprovalsPage,
                label: 'Approvals',
                section: 'approvals',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            // Back-compat redirect for the old sharing-approval URL
            {
                path: '/admin/mgt/sharing-approval',
                component: SharingApprovalRedirect,
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true
            },
            // Monitoring routes
            // Back-compat redirects for the analytics dashboards' former admin URLs. No role
            // gate: the /analytics routes they land on enforce scope themselves.
            ...[
                NavigationRoutes.admin.legacyAnalytics(),
                NavigationRoutes.admin.legacyTokenAnalytics(),
                NavigationRoutes.admin.legacyRoiAnalytics()
            ].map(
                (path): Page => ({
                    path,
                    component: LegacyAnalyticsRedirect,
                    authorize: true,
                    hideFromMenu: true
                })
            ),
            {
                path: NavigationRoutes.admin.threadRunErrors(),
                component: ThreadRunErrorsPage,
                label: 'Thread Run Errors',
                section: 'monitoring',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.threadRunDebug(
                    ':assistantId',
                    ':threadId',
                    ':runId'
                ),
                component: ThreadRunDebugPage,
                label: 'Thread Run Debug',
                section: 'monitoring',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.admin.jobs(),
                component: AdminBackgroundOperationsPage,
                label: 'Jobs',
                section: 'monitoring',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.jobDetail(':id'),
                component: AdminJobDetailPage,
                label: 'Job Details',
                section: 'monitoring',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.admin.diagnostics(),
                component: AdminDiagnosticsPage,
                label: 'File Diagnostics',
                section: 'monitoring',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.fileSyncOperations(),
                component: AdminFileSyncOperationsPage,
                label: 'File Sync Operations',
                section: 'monitoring',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.driveSyncs(),
                component: AdminDriveSyncsPage,
                label: 'Drive Syncs',
                section: 'monitoring',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            // Management routes
            {
                path: NavigationRoutes.admin.users(),
                component: AdminUsersPage,
                label: 'Users',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.userDetail(':userProfileId'),
                component: AdminUserDetailPage,
                label: 'User Details',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.admin.profileChangeLog(),
                component: AdminProfileChangeLogPage,
                label: 'Profile Log',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.groups(),
                component: AdminGroupsPage,
                label: 'Groups',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.groupDetail(':groupId'),
                component: AdminGroupDetailPage,
                label: 'Group Details',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.admin.accessProfiles(),
                component: AdminAccessProfilesPage,
                label: 'Access Profiles',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.accessProfileDetail(':profileId'),
                component: AdminAccessProfileDetailPage,
                label: 'Access Profile Details',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                hideFromMenu: true
            },
            {
                path: NavigationRoutes.admin.assistants(),
                component: AdminAssistantsPage,
                label: 'Assistants',
                section: 'mgt',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            // Tools routes
            {
                path: NavigationRoutes.admin.toolSettings(),
                component: AdminToolSettingsPage,
                label: 'Settings',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.webSearchSettings(),
                component: AdminWebSearchToolSettingsPage,
                label: 'Web Search',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.microsoftGraphApiSettings(),
                component: AdminMicrosoftGraphApiToolSettingsPage,
                label: 'Microsoft Graph API',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                featureFlag: 'graph_mcp'
            },
            {
                path: NavigationRoutes.admin.m365ToolSettings(),
                component: AdminM365ToolSettingsPage,
                label: 'M365 Tools',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.plannerToolSettings(),
                component: AdminPlannerToolSettingsPage,
                label: 'Planner',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.helpfruitSettings(),
                component: AdminHelpfruitToolSettingsPage,
                label: 'Helpfruit',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.skills(),
                component: AdminSkillsPage,
                label: 'Skills',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                featureFlag: 'skills'
            },
            {
                path: NavigationRoutes.admin.builtInToolsSettings(),
                component: AdminBuiltInToolsSettingsPage,
                label: 'Built-in',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.connectorSettings(),
                component: AdminConnectorSettingsPage,
                label: 'Connectors',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.mcpServers(),
                component: AdminMcpPage,
                label: 'MCP Servers',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.authConfigurations(),
                component: AdminAuthConfigsPage,
                label: 'Auth Configurations',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.driveSyncSettings(),
                component: AdminDriveSyncSettingsPage,
                label: 'Drive Sync',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.mcpActivityLogs(),
                component: AdminMcpActivityLogsPage,
                label: 'MCP Activity Logs',
                section: 'tools',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                isThetaAdminOnly: true
            },
            // Billing - Standalone
            {
                path: NavigationRoutes.admin.billing(),
                component: AdminBillingPage,
                label: 'Billing',
                section: 'billing',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            // Entra ID Sync routes
            {
                path: NavigationRoutes.admin.entraIdSyncConfig(),
                component: AdminEntraIdSyncConfigPage,
                label: 'Configuration',
                section: 'azureadsync',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.entraIdSyncGroupFilter(),
                component: AdminEntraIdSyncGroupFilterPage,
                label: 'Group Filter',
                section: 'azureadsync',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            {
                path: NavigationRoutes.admin.entraIdSyncHistory(),
                component: AdminEntraIdSyncHistoryPage,
                label: 'Sync & History',
                section: 'azureadsync',
                authorize: true,
                roles: [RoleNames.SysAdmin]
            },
            // System routes
            {
                path: NavigationRoutes.admin.deploymentTasks(),
                component: AdminDeploymentTasksPage,
                label: 'Deployment Tasks',
                section: 'sys',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                isThetaAdminOnly: true
            },
            {
                path: NavigationRoutes.admin.features(),
                component: AdminFeatureFlagsPage,
                label: 'Features',
                section: 'sys',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                isThetaAdminOnly: true
            },
            {
                path: NavigationRoutes.admin.vectorStores(),
                component: AdminVectorStoresPage,
                label: 'Vector Stores',
                section: 'sys',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                isThetaAdminOnly: true
            },
            {
                path: NavigationRoutes.admin.storeSettings(),
                component: AdminPublisherSettingsPage,
                label: 'Store Settings',
                section: 'sys',
                authorize: true,
                roles: [RoleNames.SysAdmin],
                isThetaAdminOnly: true,
                featureFlag: 'store'
            }
        ]
    },
    // Share link redeem route
    {
        path: '/share/thread/:token',
        component: RedeemShareLinkPage,
        authorize: true,
        hideFromMenu: true
    },
    // Assistant edit routes (have their own layout). Which items exist and who
    // sees them is declared in components/assistant/assistantEditSections.tsx;
    // every path here only mounts AssistantLayout.
    ...assistantEditRoutes,
    // Assistant detail route (has its own layout)
    {
        path: '/assistants/:id/*',
        component: AssistantPage,
        authorize: true,
        noLayout: true
    }
];

// Helper function to get all admin pages (children of /admin)
export const getAdminPages = (): Page[] => {
    const adminParent = pages.find(p => p.path === '/admin');
    return adminParent?.children || [];
};

// Analytics sub-pages (children of /analytics), in menu order
export const getAnalyticsPages = (): Page[] => {
    const analyticsParent = pages.find(
        p => p.path === NavigationRoutes.analytics.root()
    );
    return (analyticsParent?.children ?? []).filter(
        p => !p.hideFromMenu && !p.index
    );
};

// Helper function to get pages by section
export const getPagesBySection = (section: string): Page[] => {
    const adminPages = getAdminPages();
    return adminPages.filter(p => p.section === section && !p.hideFromMenu);
};

// Helper function to get a page by path
export const getPageByPath = (path: string): Page | undefined => {
    const findInPages = (pageList: Page[]): Page | undefined => {
        for (const page of pageList) {
            if (page.path === path) {
                return page;
            }
            if (page.children) {
                const found = findInPages(page.children);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    };
    return findInPages(pages);
};

// Section URL path mapping
export const sectionUrlPaths: Record<string, string> = {
    approvals: '/admin/mgt/approvals',
    monitoring: '/admin/monitoring',
    mgt: '/admin/mgt',
    config: '/admin/config',
    tools: '/admin/tools',
    billing: '/admin/billing',
    azureadsync: '/admin/entra-id-sync',
    sys: '/admin/sys'
};

// Admin section metadata, in menu order
const adminSectionMeta: {id: string; label: string; isDropdown: boolean}[] = [
    {id: 'approvals', label: 'Approvals', isDropdown: false},
    {id: 'monitoring', label: 'Monitoring', isDropdown: true},
    {id: 'mgt', label: 'Management', isDropdown: true},
    {id: 'config', label: 'Configuration', isDropdown: true},
    {id: 'tools', label: 'Tools', isDropdown: true},
    {id: 'billing', label: 'Billing', isDropdown: false},
    {id: 'azureadsync', label: 'Entra ID Sync', isDropdown: true},
    {id: 'sys', label: 'System', isDropdown: true}
];

export const getAdminSectionLabel = (sectionId?: string) =>
    adminSectionMeta.find(section => section.id === sectionId)?.label;

// Helper to get all sections with their pages (in order)
export const getAdminSections = () => {
    const adminPages = getAdminPages();

    // Build sections in order, filtering pages for each
    return adminSectionMeta
        .map(section => ({
            id: section.id,
            label: section.label,
            isDropdown: section.isDropdown,
            pages: adminPages.filter(
                p => p.section === section.id && !p.hideFromMenu && !p.index
            )
        }))
        .filter(section => section.pages.length > 0); // Only return sections with pages
};

// Helper function to check if user can access any admin page
export const canAccessAnyAdminPage = (
    isAuthorizedForResource: (resource: Authorization) => boolean,
    isEnabled: (flag: string) => boolean,
    isThetaAdmin: boolean
): boolean => {
    const adminPages = getAdminPages();

    return adminPages.some(page => {
        // Hidden pages (detail views, redirects) always sit beside a visible one, so they
        // never decide on their own whether the Admin menu shows.
        if (page.hideFromMenu) {
            return false;
        }

        // Check authorization
        if (!isAuthorizedForResource(page)) {
            return false;
        }

        // Check feature flag
        if (page.featureFlag && !isEnabled(page.featureFlag)) {
            return false;
        }

        // Check Theta admin requirement
        if (page.isThetaAdminOnly && !isThetaAdmin) {
            return false;
        }

        return true;
    });
};

export default pages;
